import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Proveedor, ProveedorResumen } from './proveedor';

export interface ProveedoresState {
  list: PaginatedList<ProveedorResumen>;
  current: Proveedor;
  actives: ProveedorResumen[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarProveedor) => Promise<boolean>;
  actualizar: (id: number, request: ActualizarProveedor) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarProveedor {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

export interface ActualizarProveedor {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useProveedoresStore = create<ProveedoresState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Proveedor,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Proveedor
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<ProveedorResumen>>(
      `/proveedores/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<ProveedorResumen>>('/proveedores/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<Proveedor>(`/proveedores/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarProveedor) => {
    const data = await makePost<RegistrarProveedor, Proveedor>('/proveedores/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Proveedor
    });

    return true;
  },
  actualizar: async (id: number, request: ActualizarProveedor) => {
    const data = await makePatch<ActualizarProveedor, Proveedor>(`/proveedores/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/proveedores/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/proveedores/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  }
}));
